import { setFulfilled, setPending, setRejected } from '@ms/yammer-data-support';
import { NetworkQuestionsEnablementMode } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import {
  FeatureAccessSettingsClientLoadFulfilledAction,
  FeatureAccessSettingsClientLoadPendingAction,
  FeatureAccessSettingsClientLoadRejectedAction,
  FeatureAccessSettingsUpdateForAudienceAction,
} from '../../domains/featureAccessSettings/actions.types';
import { NetworkQuestionSettingsKeys } from '../../domains/featureAccessSettings/editNetworkQuestionSettingsActions';
import {
  EditNetworkQuestionSettingRejectedPayload,
  EditNetworkQuestionSettingsFulfilledAction,
  EditNetworkQuestionSettingsPendingAction,
  EditNetworkQuestionSettingsRejectedAction,
} from '../../domains/featureAccessSettings/editNetworkQuestionSettingsActions.types';

import { FeatureAccessSettingsLifecycle } from './types';

export type FeatureAccessSettingsAction =
  | FeatureAccessSettingsClientLoadPendingAction
  | FeatureAccessSettingsClientLoadRejectedAction
  | FeatureAccessSettingsClientLoadFulfilledAction
  | EditNetworkQuestionSettingsFulfilledAction
  | FeatureAccessSettingsUpdateForAudienceAction
  | EditNetworkQuestionSettingsPendingAction
  | EditNetworkQuestionSettingsRejectedAction;

const initialState: FeatureAccessSettingsLifecycle = {
  pending: false,
  fulfilled: undefined,
  rejected: undefined,
};

const updateNetworkQuestionsSettings = (
  state: FeatureAccessSettingsLifecycle,
  networkQuestionsEnablementMode: NetworkQuestionsEnablementMode,
  isNetworkQuestionsAIFeaturesEnabled?: boolean
) => {
  const isAIFeaturesEnabled =
    isNetworkQuestionsAIFeaturesEnabled === undefined ? {} : { isNetworkQuestionsAIFeaturesEnabled };

  return state.fulfilled
    ? {
        ...state,
        fulfilled: {
          ...state.fulfilled,
          networkQuestionsEnablementMode,
          ...isAIFeaturesEnabled,
        },
      }
    : state;
};

const updateFeatureAccessSettingsForAudience = (
  state: FeatureAccessSettingsLifecycle,
  payload: FeatureAccessSettingsUpdateForAudienceAction['payload']
) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: { ...state.fulfilled, ...payload },
  };
};

const updateNetworkQuestionsEnablementModeWithPreviousValue = (
  state: FeatureAccessSettingsLifecycle,
  payload: EditNetworkQuestionSettingRejectedPayload
) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      ...{ [NetworkQuestionSettingsKeys.networkQuestionsEnablementMode]: payload.prevSettingValue },
    },
  };
};

const reducer: Reducer<FeatureAccessSettingsLifecycle, FeatureAccessSettingsAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'FEATURE_ACCESS_SETTINGS.LOAD_PENDING':
      return setPending();
    case 'FEATURE_ACCESS_SETTINGS.LOAD_REJECTED':
      return setRejected(action.payload.error);
    case 'FEATURE_ACCESS_SETTINGS.LOAD_FULFILLED':
      return setFulfilled(action.payload);
    case 'NETWORK_QUESTION_SETTINGS.EDIT_FULFILLED':
      return updateNetworkQuestionsSettings(
        state,
        action.networkQuestionsMode,
        action.isNetworkQuestionsAIFeaturesEnabled
      );
    case 'NETWORK_QUESTION_SETTINGS.EDIT_PENDING':
      return updateNetworkQuestionsSettings(
        state,
        action.networkQuestionsMode,
        action.isNetworkQuestionsAIFeaturesEnabled
      );
    case 'NETWORK_QUESTION_SETTINGS.EDIT_REJECTED':
      return updateNetworkQuestionsEnablementModeWithPreviousValue(state, action.payload);
    case 'FEATURE_ACCESS_SETTINGS.UPDATE_HAS_NETWORK_WIDE_AUDIENCE':
      return updateFeatureAccessSettingsForAudience(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
